import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Icon,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";
import Functions from "../../../../callable/functions";
import usePromise from "../../../util/usePromise";
import Permissoes from "../../../../models/permissoes";
import {
  IconArchive,
  IconArrowDownCircle,
  IconArrowLeft,
  IconCalculator,
  IconPresentation,
  IconReceiptTax,
  IconReportMoney,
  IconZoomCheck,
  IconTimelineEvent,
  IconProps,
} from "@tabler/icons-react";
import { Perfil } from "../../../../models/enum";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import FelixAvatar from "../../../util/ui/felixAvatar";
import { useLocation, useNavigate } from "react-router-dom";

export default function RevisarPermissoes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state = {} } = location;
  const { usuarioId }: { usuarioId: string } = state;
  const [salvando, setSalvando] = useState(false);
  const obterUsuarioAuthPromise = useCallback(
    () =>
      new Functions()
        .obterUsuarioAuth({ target: usuarioId })
        .then((res) => res.data.targetAuth)
        .catch((e) => {
          console.error(e);
        }),
    [usuarioId]
  );
  const { data: targetAuth, loading } = usePromise(obterUsuarioAuthPromise);
  const [permissoes, setPermissoes] = useState<Permissoes>(
    new Permissoes({
      ver_analises: false,
      ver_timeline: false,
      ver_imposto: false,
      gerar_darf: false,
      calcular: false,
      incluir_nota: false,
      ver_relatorios: false,
      buscar: false,
      gerenciar_usuarios: false,
      gerenciar_clientes: false,
      ver_inteligencia: false,
    })
  );

  useEffect(() => {
    if (!loading && !!targetAuth?.customClaims?.permissoes)
      setPermissoes(targetAuth.customClaims.permissoes);
  }, [loading, targetAuth?.customClaims]);

  return (
    <Container
      maxWidth="sm"
      sx={{
        px: 8,
        pt: 4,
        pb: 8,
      }}
    >
      <Box display="flex" flexDirection="row" alignItems={"center"}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems={"center"}
          paddingRight={2}
        >
          {loading ? (
            <Skeleton
              variant="circular"
              width={80}
              height={80}
              animation={false}
            />
          ) : (
            <FelixAvatar
              nome={targetAuth?.displayName ?? ""}
              size={80}
              perfil={targetAuth?.customClaims?.perfil ?? Perfil.INVESTIDOR}
              fotoDePerfil={targetAuth?.photoURL}
            />
          )}
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h3" color="text.primary">
            {loading ? (
              <Skeleton variant="text" width={200} animation={false} />
            ) : (
              targetAuth?.displayName
            )}
          </Typography>
          <Typography variant="dataMono" fontSize={13} color="text.secondary">
            {loading ? (
              <Skeleton variant="text" width={240} animation={false} />
            ) : (
              targetAuth?.email
            )}
          </Typography>
          {/* Metadado: rótulo mono + data em mono tabular. */}
          <Box display="flex" flexDirection="row" alignItems="baseline" mt={1}>
            <Typography variant="labelMono" color="text.secondary" mr={0.75}>
              criado:
            </Typography>
            <Typography variant="dataMono" fontSize={12} color="text.secondary">
              {loading ? (
                <Skeleton variant="text" width={80} animation={false} />
              ) : (
                moment(targetAuth?.metadata.creationTime).format("DD/MM/YYYY")
              )}
            </Typography>
            <Box mr={2} />
            <Typography variant="labelMono" color="text.secondary" mr={0.75}>
              último acesso:
            </Typography>
            <Typography variant="dataMono" fontSize={12} color="text.secondary">
              {loading ? (
                <Skeleton variant="text" width={80} animation={false} />
              ) : targetAuth?.metadata.lastSignInTime ? (
                moment(targetAuth?.metadata.lastSignInTime).format("DD/MM/YYYY")
              ) : (
                "-"
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" py={3}>
        <Box display="flex" flexDirection="row-reverse" alignItems="flex-start">
          <LoadingButton
            color="primary"
            variant="contained"
            disabled={loading}
            loading={salvando}
            loadingPosition="start"
            startIcon={<IconArrowDownCircle />}
            onClick={() => {
              setSalvando(true);
              new Functions()
                .atualizarPermissoes({
                  perfil: Perfil.INVESTIDOR,
                  permissoes,
                  target: usuarioId,
                })
                .finally(() => setSalvando(false));
            }}
          >
            Salvar
          </LoadingButton>
          <Box marginRight={1} />
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            startIcon={<IconArrowLeft size={18} />}
          >
            Voltar
          </Button>
        </Box>
        {/* Section header numerado: NN em mono + nome na Mabry. */}
        <Box display="flex" alignItems="baseline" gap={1.5} pt={3}>
          <Typography variant="eyebrow" color="text.secondary">
            01
          </Typography>
          <Typography variant="h3" color="text.primary">
            Telas
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" pt={0.5}>
          Personalize quais telas poderão ser acessadas
        </Typography>

        {loading ? (
          <>
            {[...Array(5)].map((_, index) => (
              <LinhaPermissaoSkeleton key={`tela-${index}`} />
            ))}
          </>
        ) : (
          <>
            <LinhaPermissao
              disabled={loading}
              titulo="Arquivo"
              icone={IconArchive}
              permissao={permissoes.incluir_nota}
              nome="incluir_nota"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  incluir_nota: checked,
                }));
              }}
              descricao="Permite ver e editar notas e conexões."
            />

            <LinhaPermissao
              disabled={loading}
              titulo="Timeline"
              icone={IconTimelineEvent}
              permissao={permissoes.ver_timeline}
              nome="ver_timeline"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  ver_timeline: checked,
                }));
              }}
              descricao="Permite acessar a tela de Timeline."
            />

            <LinhaPermissao
              disabled={loading}
              titulo="Declaração"
              icone={IconReportMoney}
              permissao={permissoes.ver_relatorios}
              nome="ver_relatorios"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  ver_relatorios: checked,
                }));
              }}
              descricao="Permite ver os Relatórios Auxiliares para a Declaração."
            />

            <LinhaPermissao
              disabled={loading}
              titulo="Análises"
              icone={IconPresentation}
              permissao={permissoes.ver_analises}
              nome="ver_analises"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  ver_analises: checked,
                }));
              }}
              descricao="Permite acessar as telas de Análises e Rentabilidade."
            />

            <LinhaPermissao
              disabled={loading}
              titulo="Consulta"
              icone={IconZoomCheck}
              permissao={permissoes.buscar}
              nome="buscar"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  buscar: checked,
                }));
              }}
              descricao="Permite acessar a tela de Consulta."
            />
          </>
        )}
        <Box display="flex" alignItems="baseline" gap={1.5} pt={3}>
          <Typography variant="eyebrow" color="text.secondary">
            02
          </Typography>
          <Typography variant="h3" color="text.primary">
            Ações
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" pt={0.5}>
          Personalize quais ações poderão ser executadas
        </Typography>

        {loading ? (
          <>
            {[...Array(3)].map((_, index) => (
              <LinhaPermissaoSkeleton key={`acao-${index}`} />
            ))}
          </>
        ) : (
          <>
            <LinhaPermissao
              disabled={loading}
              titulo="Calcular"
              icone={IconCalculator}
              permissao={permissoes.calcular}
              nome="calcular"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  calcular: checked,
                }));
              }}
              descricao="Permite executar cálculos na tela de Timeline."
            />

            <LinhaPermissao
              disabled={loading}
              titulo="Imposto"
              icone={IconReportMoney}
              permissao={permissoes.ver_imposto}
              nome="ver_imposto"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  ver_imposto: checked,
                }));
              }}
              descricao="Permite ver o imposto calculado."
            />

            <LinhaPermissao
              disabled={loading}
              titulo="DARF"
              icone={IconReceiptTax}
              permissao={permissoes.gerar_darf}
              nome="gerar_darf"
              onChange={(checked) => {
                setPermissoes((prev) => ({
                  ...prev,
                  gerar_darf: checked,
                }));
              }}
              descricao="Permite ver e emitir a DARF."
            />
          </>
        )}
      </Box>
    </Container>
  );
}
type LinhaPermissaoProps = {
  titulo: string;
  icone: React.ForwardRefExoticComponent<IconProps>;
  descricao: string;
  nome: string;
  permissao: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
};

function LinhaPermissao({
  titulo,
  icone: Icone,
  descricao,
  nome,
  permissao,
  onChange,
  disabled,
}: LinhaPermissaoProps) {
  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="flex-start" py={2}>
        <Box flexBasis="20%" flexGrow={1} flexShrink={0} pt={0.75}>
          {/* Rótulo de campo → labelMono. */}
          <Typography variant="labelMono" component="p" color="text.primary">
            {titulo}
          </Typography>
        </Box>
        <Box flexBasis="8%">
          <Icon color="action">
            <Icone />
          </Icon>
        </Box>
        <Box
          flexBasis="72%"
          flexGrow={1}
          flexShrink={0}
          display="flex"
          flexDirection="row"
        >
          <Switch
            checked={permissao}
            name={nome}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
          <Box px={1} />
          <Typography variant="body2" color="text.secondary">
            {descricao}
          </Typography>
        </Box>
      </Box>
      <Divider />
    </>
  );
}

function LinhaPermissaoSkeleton() {
  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="flex-start" py={2}>
        <Box flexBasis="20%" flexGrow={1} flexShrink={0}>
          <Skeleton variant="text" width="80%" animation={false} />
        </Box>
        <Box flexBasis="8%">
          <Skeleton
            variant="circular"
            width={24}
            height={24}
            animation={false}
          />
        </Box>
        <Box
          flexBasis="72%"
          flexGrow={1}
          flexShrink={0}
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          <Skeleton
            variant="rounded"
            width={40}
            height={24}
            animation={false}
          />
          <Box px={1} />
          <Skeleton variant="text" width="100%" animation={false} />
        </Box>
      </Box>
      <Divider />
    </>
  );
}
